import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'da', label: 'Dansk' },
  { code: 'de', label: 'Deutsch' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = i18n.language || 'en';
  const currentLangLabel = languages.find(l => l.code === currentLang)?.label || 'English';

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangOpen(false);
    const path = location.pathname;
    const newPath = path.replace(/^\/(en|fr|da|de)/, `/${lang}`);
    navigate(newPath || `/${lang}/`);
  };

  const navLinks = [
    { label: t('nav.spreadsheet'), href: `/${currentLang}/spreadsheet/` },
    { label: t('nav.categories'), href: `/${currentLang}/categories/` },
    { label: 'Products', href: `/${currentLang}/products/` },
    { label: t('nav.guides'), href: `/${currentLang}/guides/` },
    { label: t('nav.reviews'), href: `/${currentLang}/review/` },
    { label: t('nav.shipping'), href: `/${currentLang}/shipping/` },
    { label: t('nav.safety'), href: `/${currentLang}/safety/` },
    { label: t('nav.faq'), href: `/${currentLang}/faq/` },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={`/${currentLang}/`} className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-2 text-white text-lg"></i>
            </div>
            <span className={`font-heading font-bold text-lg md:text-xl transition-colors ${
              isScrolled ? 'text-foreground-950' : 'text-foreground-950'
            }`}>
              AllChinaBuy
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isScrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-white/50'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side: Language + CTA */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isScrolled
                    ? 'text-foreground-700 hover:bg-background-100'
                    : 'text-foreground-700 hover:bg-white/50'
                }`}
              >
                <i className="ri-global-line"></i>
                <span className="hidden sm:inline">{currentLangLabel}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-lg border border-background-200/70 shadow-lg overflow-hidden min-w-[160px] animate-fade-in">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                        currentLang === lang.code
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Main Website CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium whitespace-nowrap transition-all hover:bg-primary-600"
            >
              {t('nav.mainSite')}
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-foreground-700 hover:bg-background-100"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-background-200/70 animate-fade-in">
          <div className="container-main py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 rounded-lg text-foreground-700 hover:bg-primary-50 hover:text-primary-700 font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-500 text-white font-medium"
              >
                {t('nav.mainSite')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}